import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getArchivedListNextPage } from '../../actions/archivedList';
import { getChatListNextPage, setChatListLoadPageSucceed } from '../../actions/chatList';
import { getSpamListNextPage } from '../../actions/spamList';
import { getStarredChatsListNextPage } from '../../actions/starredChatsList';
import { getUnreadListNextPage } from '../../actions/unreadList';
import { ChatListLayout } from '../../components/ChatList';
import { ChatListSkeleton } from '../../components/ChatListSkeleton';
import { useChatsToDisplay } from '../../hooks/useChatsToDisplay';
import { currentOpenChatIDSelector, hostTypeSelector, chatListSelector, userSelector } from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { EHostType } from '../../types/hostType';
import { ERoute } from '../../types/route';
import { ChatListContentContainer } from '../ChatListContentContainer';
import { ChatListContextMenuContainer } from '../ChatListContextMenuContainer';
import { EmptyChatListContentContainer } from '../EmptyChatListContentContainer';
import { ListActionButtonsContainer } from '../ListActionButtonsContainer';
import { TopMenuContainer } from '../TopMenuContainer';

export const ChatListContainer: React.FC = () => {
  const dispatch = useDispatch();

  const { currentRoute, allChatsLoaded, status, isFirstLoad } = useSelector(chatListSelector);
  const hostType = useSelector(hostTypeSelector);

  const { isAuthenticated } = useSelector(userSelector);
  const currentOpenChatId = useSelector(currentOpenChatIDSelector);

  const listRef = React.useRef<HTMLDivElement>(null);
  const openedChatRef = React.useRef<HTMLDivElement>(null);

  const chatsToDisplay = useChatsToDisplay();

  const [isFirstScroll, setIsFirstScroll] = React.useState(!!currentOpenChatId);

  const getNextPage = React.useCallback(() => {
    switch (currentRoute) {
      case ERoute.Spam:
        return dispatch(getSpamListNextPage());
      case ERoute.Messages:
        return dispatch(getChatListNextPage());
      case ERoute.Archive:
        return dispatch(getArchivedListNextPage());
      case ERoute.Unread:
        return dispatch(getUnreadListNextPage());
      case ERoute.Starred:
        return dispatch(getStarredChatsListNextPage());
      default:
        return;
    }
  }, [dispatch, currentRoute]);

  const onScrollToEnd = React.useCallback(() => {
    if (allChatsLoaded || status === ERequestApiStatus.Pending) {
      return;
    }

    getNextPage();
  }, [allChatsLoaded, status, getNextPage]);

  React.useEffect(() => {
    if (status === ERequestApiStatus.Succeed) {
      dispatch(setChatListLoadPageSucceed());
    }
  }, [status, currentRoute, dispatch]);

  React.useEffect(() => {
    if (hostType !== EHostType.Frame && isAuthenticated && isFirstLoad) {
      getNextPage();
    }
  }, [status, isAuthenticated, getNextPage, hostType, isFirstLoad]);

  React.useEffect(() => {
    // при открытии чата по chatid нужно прокрутить его в видимую область
    if (
      hostType !== EHostType.Frame &&
      chatsToDisplay.length &&
      isFirstScroll &&
      !isFirstLoad &&
      openedChatRef.current
    ) {
      openedChatRef.current.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
      setIsFirstScroll(false);
    }
  }, [hostType, isFirstLoad, chatsToDisplay.length, isFirstScroll]);

  const isChatListLoading = isAuthenticated && status === ERequestApiStatus.Pending && !chatsToDisplay.length;

  return (
    <ChatListLayout
      topMenu={
        <TopMenuContainer
          isChangeListVisible={Boolean(chatsToDisplay.length && currentRoute !== ERoute.Spam && isAuthenticated)}
        />
      }
      chatListContent={
        <>
          {isChatListLoading && <ChatListSkeleton />}

          {!isChatListLoading && !chatsToDisplay.length && <EmptyChatListContentContainer />}

          {!isChatListLoading && chatsToDisplay.length > 0 && (
            <ChatListContentContainer
              onScrollToEnd={onScrollToEnd}
              chats={chatsToDisplay}
              listRef={listRef}
              openedChatRef={openedChatRef}
            />
          )}
        </>
      }
      listActionButtons={<ListActionButtonsContainer />}
      contextMenu={<ChatListContextMenuContainer listRef={listRef} />}
    />
  );
};
